/**
 * 簡易予測 (§7 一括取込) の手法 enum ⇔ ツールバー select value の相互変換、
 * 手法名の i18n ラベル解決、WMA 系判定の純関数群。
 */
import { SimpleForecastMethod } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { TFunction } from 'i18next'

/** ツールバー select の option value (Toolbar.tsx 既存実装と 1:1) */
export type SimpleForecastMethodOptionValue =
  | 'simple_copy'
  | 'same_weekday_category_copy'
  | 'weighted_avg'
  | 'same_weekday_category_weighted_avg'
  | 'upper_section_copy'

const OPTION_TO_METHOD: Record<
  SimpleForecastMethodOptionValue,
  SimpleForecastMethod
> = {
  simple_copy: SimpleForecastMethod.PREV_DAY_COPY,
  same_weekday_category_copy:
    SimpleForecastMethod.SAME_WEEKDAY_CATEGORY_PREV_WEEK_COPY,
  weighted_avg: SimpleForecastMethod.WEIGHTED_MOVING_AVG,
  same_weekday_category_weighted_avg:
    SimpleForecastMethod.SAME_WEEKDAY_CATEGORY_WEIGHTED_AVG,
  // AC-DFM-001/012/013 (単日断面の全対象日で出す — 当日/翌日/翌々日 は断面では
  // なく対象日の違いなので出し分けない、§2-6 ※ / issue #6513)。P0 は選択時に
  // 常に AC-DFM-013 の警告が発火する契約 (Design Note demand-danmen-copy.md
  // Decision 1)。
  upper_section_copy: SimpleForecastMethod.UPPER_SECTION_COPY,
}

/** select の option value → SimpleForecastMethod。空文字/不明値は undefined (= 未選択)。 */
export function optionValueToSimpleForecastMethod(
  value: string,
): SimpleForecastMethod | undefined {
  if (value === '') return undefined
  return OPTION_TO_METHOD[value as SimpleForecastMethodOptionValue]
}

/** SimpleForecastMethod → select の option value。UNSPECIFIED/未対応値は空文字。 */
export function simpleForecastMethodToOptionValue(
  method: SimpleForecastMethod | undefined,
): string {
  if (method === undefined) return ''
  const entry = Object.entries(OPTION_TO_METHOD).find(([, m]) => m === method)
  return entry?.[0] ?? ''
}

/**
 * WMA 系 (加重移動平均(短期) / 同区分加重平均) か判定する。
 * true の場合のみ設定ダイアログ (SimpleForecastSettingsDialog) を経由する (AC-005a/006a)。
 */
export function isWmaSimpleForecastMethod(
  method: SimpleForecastMethod,
): boolean {
  return (
    method === SimpleForecastMethod.WEIGHTED_MOVING_AVG ||
    method === SimpleForecastMethod.SAME_WEEKDAY_CATEGORY_WEIGHTED_AVG
  )
}

/**
 * 手法名の i18n ラベル (取込確認ダイアログ・警告メッセージの {{methodName}} に使う)。
 *
 * 上位断面コピーだけは key (= 手法の識別子) と表示名が一致しない: 画面に出す名前は
 * 取得元の断面で表すため (AC-DFM-012 / §2-6 断面別の取得元)、単日画面では
 * 「週間計画コピー」になる (週次画面は「月間計画コピー」、月間画面は「年間計画
 * コピー」だが、いずれも P1 の別画面なので key は持たない)。
 */
export function simpleForecastMethodLabel(
  method: SimpleForecastMethod,
  t: TFunction,
): string {
  switch (method) {
    case SimpleForecastMethod.PREV_DAY_COPY:
      return t('page:demandForecastDaily.toolbar.forecastMethodSimpleCopy')
    case SimpleForecastMethod.SAME_WEEKDAY_CATEGORY_PREV_WEEK_COPY:
      return t(
        'page:demandForecastDaily.toolbar.forecastMethodSameWeekdayCategoryCopy',
      )
    case SimpleForecastMethod.WEIGHTED_MOVING_AVG:
      return t('page:demandForecastDaily.toolbar.forecastMethodWeightedAvg')
    case SimpleForecastMethod.SAME_WEEKDAY_CATEGORY_WEIGHTED_AVG:
      return t(
        'page:demandForecastDaily.toolbar.forecastMethodSameWeekdayCategoryWeightedAvg',
      )
    case SimpleForecastMethod.UPPER_SECTION_COPY:
      return t(
        'page:demandForecastDaily.toolbar.forecastMethodUpperSectionCopy',
      )
    default:
      return ''
  }
}
